import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { logos } from '../data/mock';
import { Menu, X } from 'lucide-react';
import { Button } from './ui/button';

const Header = () => {
  const { language, setLanguage, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { path: '/', label: t('nav.home') },
    { path: '/about', label: t('nav.about') },
    { path: '/services', label: t('nav.services') },
    { path: '/ai-concierge', label: t('nav.aiConcierge') },
    { path: '/approach', label: t('nav.approach') },
    { path: '/contact', label: t('nav.contact') },
  ];

  const isActive = (path) => location.pathname === path;

  const handleNavClick = () => {
    // Scroll to top when navigating
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" onClick={handleNavClick} className="flex-shrink-0">
            <img
              src={isScrolled ? logos.light : logos.dark}
              alt="HITECH GROUP"
              className="h-10 w-auto transition-opacity duration-300"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={handleNavClick}
                className={`text-sm font-medium transition-colors duration-200 relative ${
                  isScrolled
                    ? isActive(item.path)
                      ? 'text-[#0066CC]'
                      : 'text-[#2B3A4D] hover:text-[#0066CC]'
                    : isActive(item.path)
                    ? 'text-white'
                    : 'text-white/80 hover:text-white'
                }`}
              >
                {item.label}
                {isActive(item.path) && (
                  <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-current rounded-full" />
                )}
              </Link>
            ))}
          </nav>

          {/* Right side: Language + CTA */}
          <div className="hidden lg:flex items-center gap-4">
            {/* Language Switcher */}
            <div className="flex items-center gap-1 text-sm">
              <button
                onClick={() => setLanguage('en')}
                className={`px-2 py-1 rounded transition-colors duration-200 ${
                  language === 'en'
                    ? isScrolled
                      ? 'bg-[#2B3A4D] text-white'
                      : 'bg-white text-[#2B3A4D]'
                    : isScrolled
                    ? 'text-[#6B7B8C] hover:text-[#2B3A4D]'
                    : 'text-white/70 hover:text-white'
                }`}
              >
                EN
              </button>
              <span className={isScrolled ? 'text-[#6B7B8C]' : 'text-white/50'}>|</span>
              <button
                onClick={() => setLanguage('fr')}
                className={`px-2 py-1 rounded transition-colors duration-200 ${
                  language === 'fr'
                    ? isScrolled
                      ? 'bg-[#2B3A4D] text-white'
                      : 'bg-white text-[#2B3A4D]'
                    : isScrolled
                    ? 'text-[#6B7B8C] hover:text-[#2B3A4D]'
                    : 'text-white/70 hover:text-white'
                }`}
              >
                FR
              </button>
            </div>

            {/* CTA Button */}
            <Link to="/contact" onClick={handleNavClick}>
              <Button
                className={`transition-all duration-200 ${
                  isScrolled
                    ? 'bg-[#0066CC] hover:bg-[#0055AA] text-white'
                    : 'bg-white text-[#2B3A4D] hover:bg-white/90'
                }`}
              >
                {t('nav.contactUs')}
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden p-2 rounded-md transition-colors duration-200 ${
              isScrolled ? 'text-[#2B3A4D]' : 'text-white'
            }`}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white rounded-lg shadow-lg mt-2 py-4 px-4 animate-in fade-in slide-in-from-top-2 duration-200">
            <nav className="flex flex-col gap-2">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    handleNavClick();
                  }}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                    isActive(item.path)
                      ? 'bg-[#F8F9FA] text-[#0066CC]'
                      : 'text-[#2B3A4D] hover:bg-[#F8F9FA]'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
            <div className="mt-4 pt-4 border-t border-[#E5E7EB]">
              <div className="flex items-center justify-between px-4">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setLanguage('en')}
                    className={`px-3 py-1.5 rounded text-sm font-medium transition-colors duration-200 ${
                      language === 'en'
                        ? 'bg-[#2B3A4D] text-white'
                        : 'text-[#6B7B8C] hover:text-[#2B3A4D]'
                    }`}
                  >
                    EN
                  </button>
                  <button
                    onClick={() => setLanguage('fr')}
                    className={`px-3 py-1.5 rounded text-sm font-medium transition-colors duration-200 ${
                      language === 'fr'
                        ? 'bg-[#2B3A4D] text-white'
                        : 'text-[#6B7B8C] hover:text-[#2B3A4D]'
                    }`}
                  >
                    FR
                  </button>
                </div>
                <Link 
                  to="/contact" 
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    handleNavClick();
                  }}
                >
                  <Button className="bg-[#0066CC] hover:bg-[#0055AA] text-white text-sm">
                    {t('nav.contactUs')}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
